import { createContext } from 'react';
import type { Theme } from '@osteopro/shared';

/**
 * Le contexte du thème, et rien d'autre.
 *
 * `createContext` produit une **identité** : deux évaluations du module donnent deux contextes
 * distincts, et un `use()` sur le second ne voit pas la valeur fournie par le premier. C'est
 * exactement ce qui se produisait au rechargement à chaud lorsque le contexte vivait dans le même
 * module que le fournisseur : Vite remplaçait le module, le fournisseur déjà monté continuait de
 * servir l'ancien contexte, et les consommateurs relisaient le nouveau — vide. D'où l'erreur
 * « useTheme doit être utilisé dans un ThemeProvider » après une simple modification de style.
 *
 * Isolé ici, ce module ne contient aucun composant ni aucun hook : il n'est donc jamais la frontière
 * d'un remplacement à chaud, et l'identité du contexte survit à toutes les modifications.
 */

export interface ThemeContextValue {
  theme: Theme;
  /** Thème réellement appliqué : `system` résolu en clair ou sombre. */
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);
